import {
  Box,
  ButtonBase,
  Divider,
  IconButton,
  InputAdornment,
  Stack,
  Tab,
  Tabs,
  TextField,
  Skeleton,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import {
  ArrowRightAltOutlined,
  Close,
  CloseOutlined,
  Download,
  MultipleStop,
  SearchOutlined,
  SignalCellularAlt,
  Upload,
} from "@mui/icons-material";
import { useEffect, useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { useMapa } from "../../../contexts/mapaContext";
import { convertBitsToBytes } from "../../../utils/easyMaps/convertToBytes";
import DraggableModal from "../draggableModal";

const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

const hasPower = (v) => typeof v === "number" && !isNaN(v) && v !== 0;
const formatDbm = (v) => (hasPower(v) ? `${v.toFixed(1)} dBm` : "Sem dados");
const formatBits = (v) => {
  const { valor, unidade } = convertBitsToBytes(v);
  return `${valor} ${unidade}`.trim();
};
const powerToPercent = (v) =>
  hasPower(v) ? clamp((40 + v) / 40, 0.02, 1) * 100 : 0;

const capacityGbps = (conn, data) =>
  conn?.config?.flagCapacidadeAutomatica
    ? (data?.capacidade_zabbix ?? 0) / 1e9
    : Number(conn?.config?.capacidade ?? 0);
const percentOf = (bits, cap) =>
  cap > 0 && bits ? (Number(bits) / 1e9 / cap) * 100 : null;

const interfaceName = (conn, data) =>
  conn?.config?.interfaceName?.trim() || data?.interface || "";

const mono = { fontFamily: "'Roboto Mono', monospace" };
const ellipsis = {
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  minWidth: 0,
};

const BarRow = ({ icon: Icon, label, percent, color, value, detail }) => {
  const theme = useTheme();
  return (
    <>
      <Icon aria-label={label} sx={{ fontSize: 14, color }} />
      <Box
        sx={{
          height: 6,
          borderRadius: 3,
          overflow: "hidden",
          backgroundColor: alpha(theme.palette.text.primary, 0.1),
        }}
      >
        <Box
          sx={{
            height: "100%",
            borderRadius: 3,
            width: `${percent == null ? 0 : clamp(percent, 2, 100)}%`,
            backgroundColor: color,
          }}
        />
      </Box>
      <Typography
        sx={{
          ...mono,
          fontSize: "0.7rem",
          textAlign: "right",
          whiteSpace: "nowrap",
        }}
      >
        {detail && (
          <Box
            component="span"
            sx={{ color: theme.palette.text.secondary, mr: 0.75 }}
          >
            {detail}
          </Box>
        )}
        <Box component="span" sx={{ fontWeight: 500 }}>
          {value}
        </Box>
      </Typography>
    </>
  );
};

const EnlacesPanel = ({ open, onClose, onFocusConnection }) => {
  const theme = useTheme();
  const {
    connections,
    connectionsDataMap,
    elements,
    mapId,
    tipo,
    lastCollectData,
  } = useMapa();
  const loading = !lastCollectData;
  const outlet = useOutletContext();
  const mapLayer = outlet?.configLayer?.[String(mapId)] || "trafego";

  const [layer, setLayer] = useState(mapLayer);
  const [query, setQuery] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  useEffect(() => setLayer(mapLayer), [mapLayer]);

  const isTraffic = layer !== "potencia";

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = [];
    connections?.forEach((conn) => {
      const data = connectionsDataMap?.get(conn.id);
      const origem = elements?.get(conn.referenceId)?.titulo ?? "";
      const destino = elements?.get(conn.destinationId)?.titulo ?? "";
      const iface = interfaceName(conn, data);
      if (q && !`${origem} ${iface} ${destino}`.toLowerCase().includes(q))
        return;
      list.push({ id: conn.id, conn, origem, destino, iface, data });
    });
    return list.sort((a, b) => {
      if (isTraffic)
        return (b.data?.percentTraffic ?? -1) - (a.data?.percentTraffic ?? -1);
      const ra = hasPower(a.data?.rx_power) ? a.data.rx_power : Infinity;
      const rb = hasPower(b.data?.rx_power) ? b.data.rx_power : Infinity;
      return ra - rb; // pior sinal primeiro, sem dados no fim
    });
  }, [connections, connectionsDataMap, elements, query, isTraffic]);

  const configTipo = outlet?.config?.configuracoes?.[tipo];
  const escala =
    (isTraffic
      ? configTipo?.camadaTrafego?.escala
      : configTipo?.camadaPotencia?.escala) ?? [];

  const offline = theme.palette.enlaces?.offline?.main ?? theme.palette.divider;
  return (
    <DraggableModal
      open={open}
      onClose={onClose}
      title="Enlaces"
      storageKey="easyMapsEnlacesPanel"
    >
      <Stack gap={1.25} sx={{ px: 1.5, pb: 1.25 }}>
        <Stack
          sx={{
            borderRadius: "5px",
            backgroundColor: theme.palette.background.paperSelectedMaps,
            boxShadow: "0px 0px 3px  #0000001a",
            border: "none",
            "&::before": {
              display: "none",
            },
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "start",
            p: 0.5,
            mt: 2,
            height: "50px",
          }}
        >
          <Tabs
            value={layer}
            onChange={(e, next) => setLayer(next)}
            variant="fullWidth"
            textColor="primary"
            sx={{
              width: "100%",
              minHeight: "20px",
              "& button": {
                color: theme.palette.text.disabled,
                textTransform: "none",
                minHeight: "20px",
                Height: "100%",
                borderRadius: "5px",
                paddingX: "10px",
              },
              "& .MuiTab-icon": { marginRight: "2px" },
              "& button.Mui-selected": {
                backgroundColor: theme.palette.background.tab,
              },
              "& .MuiTabs-indicator": {
                backgroundColor: "transparent",
                border: "none",
              },
            }}
          >
            <Tab
              value="trafego"
              label="Tráfego"
              icon={<MultipleStop />}
              sx={{ fontSize: "0.875rem", color: theme.palette.color.text }}
              iconPosition="start"
            />
            <Tab
              value="potencia"
              label="Potência"
              icon={<SignalCellularAlt />}
              iconPosition="start"
              sx={{ fontSize: "0.875rem", color: theme.palette.color.text }}
            />
          </Tabs>
        </Stack>
        <TextField
          size="small"
          fullWidth
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Buscar host ou interface"
          inputProps={{ "aria-label": "Buscar enlace" }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchOutlined fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: query ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  aria-label="Limpar busca"
                  onClick={() => setQuery("")}
                >
                  <CloseOutlined sx={{ fontSize: 16 }} />
                </IconButton>
              </InputAdornment>
            ) : null,
            sx: { fontSize: "0.82rem" },
          }}
        />
        {escala.length > 0 && (
          <Stack direction="row" aria-label="Escala do mapa">
            {escala.map((s, i) => (
              <Stack key={i} gap={0.4} sx={{ flex: 1, minWidth: 0 }}>
                <Box
                  sx={{
                    height: 10,
                    backgroundColor: s.cor,
                  }}
                />
              </Stack>
            ))}
          </Stack>
        )}
      </Stack>

      <Divider />

      <Box
        component="ol"
        aria-label="Lista de enlaces"
        sx={{ listStyle: "none", m: 0, p: 0, flex: 1, overflowY: "auto" }}
      >
        {loading &&
          [0, 1, 2, 3, 4, 5, 6].map((i) => (
            <Stack
              key={i}
              component="li"
              aria-hidden="true"
              gap={0.75}
              sx={{
                px: 1.5,
                py: 1.25,
                borderBottom: `1px solid ${theme.palette.divider}`,
              }}
            >
              <Stack direction="row" gap={1}>
                <Skeleton variant="text" width="40%" />
                <Skeleton variant="text" width="35%" />
              </Stack>
              <Skeleton variant="rounded" height={6} sx={{ ml: 3.25 }} />
              <Skeleton variant="rounded" height={6} sx={{ ml: 3.25 }} />
            </Stack>
          ))}

        {!loading &&
          rows.map((r, i) => {
            const d = r.data;
            const cap = capacityGbps(r.conn, d);
            const selected = selectedId === r.id;
            return (
              <li key={r.id}>
                <Tooltip
                  title={`${r.origem}${r.iface ? ` (${r.iface})` : ""} → ${r.destino}`}
                  placement="left"
                  enterDelay={600}
                >
                  <ButtonBase
                    onClick={() => {
                      setSelectedId(r.id);
                      onFocusConnection?.(r.conn);
                    }}
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "stretch",
                      gap: 0.75,
                      width: "100%",
                      textAlign: "left",
                      px: 1.5,
                      py: 1.25,
                      borderBottom: `1px solid ${theme.palette.divider}`,
                      boxShadow: selected
                        ? `inset 3px 0 0 ${theme.palette.primary.main}`
                        : "none",
                      "&:hover": {
                        backgroundColor: theme.palette.background.hover,
                      },
                    }}
                  >
                    <Stack direction="row" alignItems="flex-start" gap={0.75}>
                      <Typography
                        sx={{
                          ...mono,
                          fontSize: "0.7rem",
                          color: theme.palette.text.secondary,
                          width: 18,
                          flexShrink: 0,
                          lineHeight: "20px",
                        }}
                      >
                        {i + 1}
                      </Typography>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          sx={{
                            fontSize: "0.82rem",
                            fontWeight: 500,
                            lineHeight: "20px",
                            ...ellipsis,
                          }}
                        >
                          {r.origem}
                        </Typography>
                        {r.iface && (
                          <Typography
                            sx={{
                              ...mono,
                              fontSize: "0.7rem",
                              color: theme.palette.text.secondary,
                              ...ellipsis,
                            }}
                          >
                            {r.iface}
                          </Typography>
                        )}
                      </Box>
                      <ArrowRightAltOutlined
                        sx={{
                          fontSize: 20,
                          color: theme.palette.text.disabled,
                          flexShrink: 0,
                        }}
                      />
                      <Typography
                        sx={{
                          flex: 1,
                          fontSize: "0.82rem",
                          lineHeight: "20px",
                          color: theme.palette.text.secondary,
                          ...ellipsis,
                        }}
                      >
                        {r.destino}
                      </Typography>
                    </Stack>

                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "18px minmax(0, 1fr) auto",
                        alignItems: "center",
                        columnGap: 1,
                        rowGap: 0.5,
                        pl: 3.25,
                      }}
                    >
                      {d?.isDown ? (
                        <Stack
                          direction="row"
                          alignItems="center"
                          gap={0.5}
                          sx={{
                            gridColumn: "1 / -1",
                            color: theme.palette.error.main,
                          }}
                        >
                          <Close sx={{ fontSize: 16 }} />
                          <Typography
                            sx={{ fontSize: "0.75rem", fontWeight: 700 }}
                          >
                            Offline
                          </Typography>
                        </Stack>
                      ) : isTraffic ? (
                        <>
                          <BarRow
                            icon={Download}
                            label="Rx"
                            percent={percentOf(d?.rx_traffic, cap)}
                            color={
                              d?.corTrafficRx?.cor ??
                              d?.corTraffic?.cor ??
                              offline
                            }
                            detail={formatBits(d?.rx_traffic)}
                            value={
                              percentOf(d?.rx_traffic, cap) == null
                                ? "--"
                                : `${Math.round(percentOf(d?.rx_traffic, cap))}%`
                            }
                          />
                          <BarRow
                            icon={Upload}
                            label="Tx"
                            percent={percentOf(d?.tx_traffic, cap)}
                            color={
                              d?.corTrafficTx?.cor ??
                              d?.corTraffic?.cor ??
                              offline
                            }
                            detail={formatBits(d?.tx_traffic)}
                            value={
                              percentOf(d?.tx_traffic, cap) == null
                                ? "--"
                                : `${Math.round(percentOf(d?.tx_traffic, cap))}%`
                            }
                          />
                        </>
                      ) : (
                        <>
                          <BarRow
                            icon={Download}
                            label="Rx"
                            percent={powerToPercent(d?.rx_power)}
                            color={
                              d?.corPowerRx?.cor ?? d?.corPower?.cor ?? offline
                            }
                            value={formatDbm(d?.rx_power)}
                          />
                          <BarRow
                            icon={Upload}
                            label="Tx"
                            percent={powerToPercent(d?.tx_power)}
                            color={
                              d?.corPowerTx?.cor ?? d?.corPower?.cor ?? offline
                            }
                            value={formatDbm(d?.tx_power)}
                          />
                        </>
                      )}
                    </Box>
                  </ButtonBase>
                </Tooltip>
              </li>
            );
          })}
        {!loading && rows.length === 0 && (
          <Typography
            component="li"
            sx={{
              py: 4,
              textAlign: "center",
              fontSize: "0.82rem",
              color: theme.palette.text.secondary,
            }}
          >
            {query
              ? `Nenhum enlace encontrado para “${query}”.`
              : "Nenhum enlace neste mapa."}
          </Typography>
        )}
      </Box>
    </DraggableModal>
  );
};

export default EnlacesPanel;
